import React, { useState, useEffect, useContext, useCallback } from 'react';
import classNames from 'classnames/bind';
import styles from './RewardManagement.module.css';
import { ToastContext } from '~/components/context/ToastContext';
import { ModalContext } from '~/components/context/ModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSave, faSyncAlt, faEdit, faHistory, faRedo } from '@fortawesome/free-solid-svg-icons';
import { BASE_LUCKY_URL } from '~/helper/apiService';

const cx = classNames.bind(styles);

function RewardManagement() {
  const [rewards, setRewards] = useState([]);
  const [newRewardName, setNewRewardName] = useState('');
  const [newRewardQuantity, setNewRewardQuantity] = useState(0);
  const [editingReward, setEditingReward] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  // Fetch rewards
  const fetchRewards = useCallback(async () => {
    try {
      setRewardsLoading(true);
      const response = await fetch(`${BASE_LUCKY_URL}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setRewards(data || []);
      } else {
        showToast(data.error || 'Lỗi khi lấy thông tin phần thưởng', 'error');
      }
    } catch (err) {
      showToast('Lỗi kết nối đến server: ' + err.message, 'error');
    } finally {
      setRewardsLoading(false);
    }
  }, [showToast]);

  // Fetch rewards on mount
  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  // Handle adding new reward
  const handleAddReward = useCallback(async () => {
    if (!newRewardName) {
      showToast('Vui lòng nhập tên phần thưởng', 'error');
      return;
    }
    if (!Number.isInteger(Number(newRewardQuantity)) || Number(newRewardQuantity) < -1) {
      showToast('Số lượng phải là số nguyên >= -1', 'error');
      return;
    }

    const tempId = Date.now();
    const newReward = {
      id: tempId,
      name: newRewardName,
      quantity: Number(newRewardQuantity),
    };
    setRewards((prev) => [...prev, newReward]);

    try {
      setLoading(true);
      const response = await fetch(`${BASE_LUCKY_URL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newRewardName,
          quantity: Number(newRewardQuantity),
        }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Tạo phần thưởng thành công', 'success');
        fetchRewards();
        setNewRewardName('');
        setNewRewardQuantity(0);
      } else {
        showToast(data.error || 'Lỗi khi tạo phần thưởng', 'error');
        setRewards((prev) => prev.filter((reward) => reward.id !== tempId));
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
      setRewards((prev) => prev.filter((reward) => reward.id !== tempId));
    } finally {
      setLoading(false);
    }
  }, [newRewardName, newRewardQuantity, showToast, fetchRewards]);

  // Handle editing reward
  const handleEditReward = useCallback(async (reward) => {
    if (!reward.name) {
      showToast('Vui lòng nhập tên phần thưởng', 'error');
      return;
    }
    if (!Number.isInteger(Number(reward.quantity)) || Number(reward.quantity) < -1) {
      showToast('Số lượng phải là số nguyên >= -1', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${BASE_LUCKY_URL}${reward.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: reward.name,
          quantity: Number(reward.quantity),
        }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Cập nhật phần thưởng thành công', 'success');
        fetchRewards();
        setEditingReward(null);
      } else {
        showToast(data.error || 'Lỗi khi cập nhật phần thưởng', 'error');
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, fetchRewards]);

  // Handle deleting reward
  const handleDeleteReward = useCallback(async (id) => {
    const deletedReward = rewards.find((reward) => reward.id === id);
    setRewards((prev) => prev.filter((reward) => reward.id !== id));

    try {
      setLoading(true);
      const response = await fetch(`${BASE_LUCKY_URL}${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Xóa phần thưởng thành công', 'success');
        fetchRewards();
      } else {
        showToast(data.error || 'Lỗi khi xóa phần thưởng', 'error');
        setRewards((prev) => [...prev, deletedReward]);
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
      setRewards((prev) => [...prev, deletedReward]);
    } finally {
      setLoading(false);
    }
  }, [rewards, showToast, fetchRewards]);

  // Confirm delete reward
  const confirmDeleteReward = useCallback(
    (reward) => {
      showModal({
        title: 'Xác Nhận Xóa',
        message: `Bạn có chắc chắn muốn xóa phần thưởng ${reward.name}?`,
        onConfirm: () => handleDeleteReward(reward.id),
        onCancel: null,
      });
    },
    [showModal, handleDeleteReward]
  );

  // Handle reloading reward data
  const handleReloadRewards = useCallback(async () => {
    await fetchRewards();
    showToast('Tải lại dữ liệu phần thưởng thành công', 'success');
  }, [fetchRewards, showToast]);

  // Handle deleting history
  const handleDeleteHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_LUCKY_URL}history`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.text();
        throw new Error(data || `HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      showToast(data.message || 'Xóa lịch sử thành công', 'success');
    } catch (error) {
      showToast('Lỗi khi xóa lịch sử: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Confirm delete history
  const confirmDeleteHistory = useCallback(() => {
    showModal({
      title: 'Xác Nhận Xóa Lịch Sử',
      message: 'Bạn có chắc chắn muốn xóa toàn bộ lịch sử? Tất cả các số điện thoại sẽ được đăng ký lại',
      onConfirm: handleDeleteHistory,
      onCancel: null,
    });
  }, [showModal, handleDeleteHistory]);

  // Handle deleting all spins
  const handleDeleteSpins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_LUCKY_URL}reset`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.text();
        throw new Error(data || `HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      showToast(data.message || 'Xóa toàn bộ lượt quay thành công', 'success');
    } catch (error) {
      showToast('Lỗi khi xóa lượt quay: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Confirm delete spins
  const confirmDeleteSpins = useCallback(() => {
    showModal({
      title: 'Xác Nhận Xóa Lượt Quay',
      message: 'Bạn có chắc chắn muốn xóa toàn bộ lượt quay? Tất cả thiết bị sẽ được phép quay lại.',
      onConfirm: handleDeleteSpins,
      onCancel: null,
    });
  }, [showModal, handleDeleteSpins]);

  return (
    <div className={cx('content')}>
      <div className={cx('reward-section')}>
        <div className={cx('reward-form')}>
          <h3 className={cx('section-title')}>
            {editingReward ? 'Chỉnh Sửa Phần Thưởng' : 'Thêm Phần Thưởng Mới'}
          </h3>
          <div className={cx('form-row')}>
            <label className={cx('form-label')}>Tên Phần Thưởng:</label>
            <input
              type="text"
              value={editingReward ? editingReward.name : newRewardName}
              onChange={(e) =>
                editingReward
                  ? setEditingReward({ ...editingReward, name: e.target.value })
                  : setNewRewardName(e.target.value)
              }
              className={cx('input')}
              placeholder="Nhập tên phần thưởng"
            />
          </div>
          <div className={cx('form-row')}>
            <label className={cx('form-label')}>Số Lượng:</label>
            <input
              type="number"
              value={editingReward ? editingReward.quantity : newRewardQuantity}
              onChange={(e) =>
                editingReward
                  ? setEditingReward({ ...editingReward, quantity: e.target.value })
                  : setNewRewardQuantity(e.target.value)
              }
              className={cx('input')}
              min="-1"
              placeholder="Nhập số lượng"
            />
          </div>
          <div className={cx('form-actions')}>
            <button
              onClick={() => editingReward ? handleEditReward(editingReward) : handleAddReward()}
              disabled={loading}
              className={cx('button', 'save-button')}
            >
              <FontAwesomeIcon icon={faSave} className={cx('button-icon')} />
              <span className={cx('button-text')}>
                {editingReward ? 'Cập Nhật' : 'Thêm Mới'}
              </span>
            </button>
            {editingReward && (
              <button
                onClick={() => setEditingReward(null)}
                disabled={loading}
                className={cx('button', 'stop-button')}
              >
                <FontAwesomeIcon icon={faTrash} className={cx('button-icon')} />
                <span className={cx('button-text')}>Hủy</span>
              </button>
            )}
          </div>
        </div>
        <div className={cx('rewards-list')}>
          <div className={cx('list-header')}>
            <h3 className={cx('section-title')}>Danh Sách Phần Thưởng</h3>
            <div className={cx('list-actions')}>
              <button
                onClick={handleReloadRewards}
                disabled={loading}
                className={cx('button', 'reload-button')}
              >
                <FontAwesomeIcon icon={faSyncAlt} className={cx('button-icon')} />
                <span className={cx('button-text')}>Tải lại</span>
              </button>
              <button
                onClick={confirmDeleteHistory}
                disabled={loading}
                className={cx('button', 'history-button')}
              >
                <FontAwesomeIcon icon={faHistory} className={cx('button-icon')} />
                <span className={cx('button-text')}>Xóa Lịch Sử</span>
              </button>
              <button
                onClick={confirmDeleteSpins}
                disabled={loading}
                className={cx('button', 'reset-button')}
              >
                <FontAwesomeIcon icon={faRedo} className={cx('button-icon')} />
                <span className={cx('button-text')}>Xóa Lượt Quay</span>
              </button>
            </div>
          </div>
          {rewardsLoading && rewards.length === 0 ? (
            <p className={cx('loading')}>Đang tải...</p>
          ) : rewards.length === 0 ? (
            <p className={cx('no-data')}>Không có phần thưởng nào</p>
          ) : (
            rewards.map((reward, index) => (
              <div key={reward.id} className={cx('reward-card')}>
                <div className={cx('card-info')}>
                  <span className={cx('card-label')}>STT: {index + 1}</span>
                  <span className={cx('card-value')}>Tên: {reward.name}</span>
                  <span className={cx('card-value')}>Số Lượng: {reward.quantity}</span>
                </div>
                <div className={cx('card-actions')}>
                  <button
                    onClick={() => setEditingReward(reward)}
                    disabled={loading}
                    className={cx('button', 'edit-button')}
                  >
                    <FontAwesomeIcon icon={faEdit} className={cx('button-icon')} />
                    <span className={cx('button-text')}>Sửa</span>
                  </button>
                  <button
                    onClick={() => confirmDeleteReward(reward)}
                    disabled={loading}
                    className={cx('button', 'stop-button')}
                  >
                    <FontAwesomeIcon icon={faTrash} className={cx('button-icon')} />
                    <span className={cx('button-text')}>Xóa</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default RewardManagement;